import React, { useEffect, useState } from "react";
import {
  Instagram,
  Facebook,
  Youtube,
  HardDrive,
  Music2,
  Linkedin,
  AtSign,
  Plug,
  Loader2,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";

const fmt = (n) => Number(n || 0).toLocaleString();

// A live "are my channels wired in?" board for the Overview tab.
const ChannelHealth = ({ onJump }) => {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [google, setGoogle] = useState(null);
  const [tiktok, setTiktok] = useState(null);
  const [youtube, setYoutube] = useState(null);
  const [linkedin, setLinkedin] = useState(null);
  const [threads, setThreads] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, g, t, y, l, th] = await Promise.all([
        api.get("/connections/meta/status").catch(() => null),
        api.get("/integrations/google/status").catch(() => null),
        api.get("/connections/tiktok/status").catch(() => null),
        api.get("/connections/youtube/status").catch(() => null),
        api.get("/connections/linkedin/status").catch(() => null),
        api.get("/connections/threads/status").catch(() => null),
      ]);
      setMeta(m?.data || null);
      setGoogle(g?.data || null);
      setTiktok(t?.data || null);
      setYoutube(y?.data || null);
      setLinkedin(l?.data || null);
      setThreads(th?.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const yt = youtube?.channel;

  const rows = [
    {
      id: "instagram",
      label: "Instagram",
      icon: Instagram,
      connected: !!meta?.instagram_connected,
      configured: !!meta?.configured,
      detail: (meta?.instagram?.accounts || [])
        .map((a) => `@${a.username || a.ig_user_id}`)
        .join(", "),
      loginUrl: `${API_BASE}/connections/meta/login`,
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: Facebook,
      connected: !!meta?.facebook_connected,
      configured: !!meta?.configured,
      detail: (meta?.facebook?.pages || [])
        .map((p) => p.page_name || p.page_id)
        .join(", "),
      loginUrl: `${API_BASE}/connections/meta/login`,
    },
    {
      id: "youtube",
      label: "YouTube",
      icon: Youtube,
      connected: !!youtube?.connected,
      configured: !!youtube?.configured,
      detail: yt
        ? `${yt.hidden_subscriber_count ? "Subs hidden" : `${fmt(yt.subscriber_count)} subs`} · ${fmt(
            yt.video_count
          )} videos · ${fmt(yt.view_count)} views`
        : "",
      loginUrl: `${API_BASE}/connections/youtube/login`,
    },
    {
      id: "tiktok",
      label: "TikTok",
      icon: Music2,
      connected: !!tiktok?.connected,
      configured: !!tiktok?.configured,
      detail: tiktok?.display_name ? `@${tiktok.display_name}` : "",
      loginUrl: `${API_BASE}/connections/tiktok/login`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      connected: !!linkedin?.connected,
      configured: !!linkedin?.configured,
      detail: linkedin?.name || "",
      loginUrl: `${API_BASE}/connections/linkedin/login`,
    },
    {
      id: "threads",
      label: "Threads",
      icon: AtSign,
      connected: !!threads?.connected,
      configured: !!threads?.configured,
      detail: threads?.username ? `@${threads.username}` : "",
      loginUrl: `${API_BASE}/connections/threads/login`,
    },
    {
      id: "google_drive",
      label: "Google Drive",
      icon: HardDrive,
      connected: !!google?.connected,
      configured: !!google?.configured,
      detail: google?.email || "",
      loginUrl: `${API_BASE}/integrations/google/login`,
    },
  ];

  const connectedCount = rows.filter((r) => r.connected).length;

  return (
    <div
      data-testid="channel-health-widget"
      className="p-5 bg-[#0A192F] border border-white/10 rounded-sm"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Plug size={16} className="text-cyan-300" />
          <h2 className="font-heading font-bold text-white text-lg">Channel Health</h2>
          <span
            data-testid="channel-health-count"
            className="px-2 py-0.5 bg-cyan-400/10 text-cyan-300 text-[10px] font-mono uppercase tracking-widest border border-cyan-400/20 rounded-sm"
          >
            {connectedCount}/{rows.length} live
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            data-testid="channel-health-refresh"
            disabled={loading}
            title="Refresh"
            className="text-slate-400 hover:text-cyan-300 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button
            onClick={() => onJump?.("connections")}
            data-testid="channel-health-manage"
            className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 hover:text-white flex items-center gap-1"
          >
            Manage <ArrowRight size={12} />
          </button>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <div
              key={r.id}
              data-testid={`channel-health-${r.id}`}
              className={[
                "p-3 rounded-sm border flex items-start gap-3 transition-colors",
                r.connected
                  ? "bg-[#050B14] border-emerald-400/30"
                  : "bg-[#050B14] border-white/10",
              ].join(" ")}
            >
              <Icon size={18} className={r.connected ? "text-emerald-300" : "text-slate-500"} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-heading font-bold text-white text-sm">{r.label}</p>
                  <span
                    data-testid={`channel-health-dot-${r.id}`}
                    className={[
                      "h-2 w-2 rounded-full",
                      r.connected ? "bg-emerald-400 animate-pulse" : r.configured ? "bg-amber-400" : "bg-slate-600",
                    ].join(" ")}
                  />
                </div>
                {r.connected ? (
                  <p className="mt-0.5 text-[11px] text-slate-400 truncate" title={r.detail}>
                    {r.detail || "Connected"}
                  </p>
                ) : (
                  <a
                    href={r.configured ? r.loginUrl : undefined}
                    onClick={(e) => {
                      if (!r.configured) {
                        e.preventDefault();
                        onJump?.("connections");
                      }
                    }}
                    data-testid={`channel-health-connect-${r.id}`}
                    className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-cyan-300 hover:text-white"
                  >
                    Connect <ArrowRight size={11} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChannelHealth;
